import type { LoginResponse, LoginUser, Permissions } from '@/shared/api';
import { apiRequest } from '@/shared/api';

export type { LoginUser, Permissions };

export interface CreateInviteResponse {
  token: string;
  expires_at: string;
  role_key: string;
  invite_path: string;
}

export interface PendingInvite {
  id: string;
  role_key: string;
  expires_at: string;
  created_at: string;
}

export interface InvitePreview {
  role_key: string;
  expires_at: string;
}

export interface UpdateMePayload {
  name?: string;
  current_password?: string;
  new_password?: string;
  locale?: 'en' | 'ru';
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  return apiRequest<LoginResponse>('/auth/login', {
    method: 'POST',
    body: { email, password },
    auth: false,
  });
}

export async function pingAuth(): Promise<{
  ok: boolean;
  user_id: string;
  email: string;
  role: string;
}> {
  return apiRequest('/auth/ping');
}

export async function getMe(): Promise<LoginUser> {
  return apiRequest<LoginUser>('/auth/me');
}

export async function updateMe(payload: UpdateMePayload): Promise<LoginUser> {
  return apiRequest<LoginUser>('/auth/me', {
    method: 'PATCH',
    body: payload,
  });
}

export async function createInvite(roleKey: string): Promise<CreateInviteResponse> {
  return apiRequest<CreateInviteResponse>('/auth/invites', {
    method: 'POST',
    body: { role_key: roleKey },
  });
}

export async function listInvites(): Promise<PendingInvite[]> {
  const data = await apiRequest<{ invites: PendingInvite[] }>('/auth/invites');
  return data.invites;
}

export async function getInvite(token: string): Promise<InvitePreview> {
  return apiRequest<InvitePreview>(`/auth/invites/${encodeURIComponent(token)}`, {
    auth: false,
  });
}

export interface OperatorAccount {
  id: string;
  name: string;
  email: string;
  role_key: string;
  locale: string;
  created_at: string;
}

export interface OperatorRole {
  key: string;
  name: string;
  description: string;
  permissions: Permissions;
}

export async function listOperators(): Promise<OperatorAccount[]> {
  const data = await apiRequest<{ users: OperatorAccount[] }>('/auth/users');
  return data.users ?? [];
}

export async function updateOperator(
  id: string,
  payload: { name?: string; role_key?: string },
): Promise<OperatorAccount> {
  return apiRequest<OperatorAccount>(`/auth/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function listRoles(): Promise<OperatorRole[]> {
  const data = await apiRequest<{ roles: OperatorRole[] }>('/auth/roles');
  return data.roles ?? [];
}

export interface AmoCRMUserAccount {
  amocrm_user_id: number;
  name: string;
  email: string;
  is_active: boolean;
  is_admin: boolean;
  synced_at: string;
  agata_user_id: string | null;
  agata_user_name?: string;
  agata_user_email?: string;
}

export async function listAmoCRMUsers(): Promise<AmoCRMUserAccount[]> {
  const data = await apiRequest<{ users: AmoCRMUserAccount[] }>('/auth/users/amocrm');
  return data.users ?? [];
}

export async function linkAmoCRMUser(
  amocrmUserId: number,
  agataUserId: string | null,
): Promise<void> {
  await apiRequest(`/auth/users/amocrm/${encodeURIComponent(String(amocrmUserId))}`, {
    method: 'PATCH',
    body: { agata_user_id: agataUserId },
  });
}

export async function acceptInvite(
  token: string,
  email: string,
  password: string,
): Promise<LoginResponse> {
  return apiRequest<LoginResponse>(`/auth/invites/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
    body: { email, password },
    auth: false,
  });
}
